import { useState, useEffect, useCallback } from 'react';

const cities = [
  'București', 'Cluj-Napoca', 'Timișoara', 'Iași', 'Constanța',
  'Craiova', 'Brașov', 'Galați', 'Ploiești', 'Oradea',
  'Brăila', 'Arad', 'Pitești', 'Sibiu', 'Bacău',
  'Târgu Mureș', 'Baia Mare', 'Buzău', 'Botoșani', 'Satu Mare',
  'Suceava', 'Piatra Neamț', 'Drobeta-Turnu Severin', 'Târgu Jiu', 'Târgoviște'
];

const names = [
  'Andrei', 'Maria', 'Alexandru', 'Elena', 'Mihai',
  'Ioana', 'Gabriel', 'Ana', 'Cristian', 'Diana',
  'Adrian', 'Laura', 'Daniel', 'Monica', 'Stefan',
  'Andreea', 'Ionut', 'Carmen', 'Florin', 'Roxana',
  'Bogdan', 'Simona', 'Radu', 'Alina', 'Marius',
  'Daniela', 'Catalin', 'Cristina', 'George', 'Alexandra'
];

const messages = [
  'tocmai a comandat cartea',
  'a început transformarea cu ajutorul cărții',
  'a făcut primul pas spre vindecare',
  'a ales să-și transforme viața',
  'a decis să învingă depresia',
  'a început călătoria spre vindecare',
  'a ales să devină mai puternic/ă',
  'a spus DA schimbării',
  'a ales să-și recapete controlul asupra vieții',
  'a început procesul de transformare'
];

interface Notification {
  id: number;
  name: string;
  city: string;
  message: string;
  time: number;
}

export function LiveOrderNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [visible, setVisible] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Generate a new notification
  const generateNotification = useCallback((): Notification => ({
    id: Date.now(),
    name: names[Math.floor(Math.random() * names.length)],
    city: cities[Math.floor(Math.random() * cities.length)],
    message: messages[Math.floor(Math.random() * messages.length)],
    time: 0
  }), []);

  useEffect(() => {
    let mounted = true;
    let timeInterval: number;
    let notificationInterval: number;

    // Add initial notifications with delay
    const addInitialNotifications = async () => {
      try {
        if (!mounted) return;

        const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
        
        // Add first notification after 5 seconds
        await delay(5000);
        if (!mounted) return;
        setNotifications([generateNotification()]);
        setVisible(true);
        
        // Add second notification after 3 more seconds
        await delay(10000);
        if (!mounted) return;
        setNotifications(prev => [...prev.slice(-2), generateNotification()]);
        
        // Add third notification after 4 more seconds
        await delay(20000);
        if (!mounted) return;
        setNotifications(prev => [...prev.slice(-2), generateNotification()]);
        
        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing notifications:', error);
      }
    };

    // Only start if not already initialized
    if (!isInitialized) {
      addInitialNotifications();
    }

    // Update time counters every second
    timeInterval = window.setInterval(() => {
      if (!mounted) return;
      setNotifications(prev => 
        prev.map(notif => ({
          ...notif,
          time: notif.time + 1
        }))
      );
    }, 1000);

    // Add new notification every 15-30 seconds
    notificationInterval = window.setInterval(() => {
      if (!mounted) return;
      try {
        setNotifications(prev => {
          const newNotif = generateNotification();
          return [...prev.slice(-2), newNotif];
        });
        
        // Show notification
        setVisible(true);
        
        // Hide after 4 seconds
        setTimeout(() => {
          if (mounted) {
            setVisible(false);
          }
        }, 4000);
      } catch (error) {
        console.error('Error generating new notification:', error);
      }
    }, Math.random() * 10000 + 10000); // Random between 10-20 seconds

    // Cleanup function
    return () => {
      mounted = false;
      clearInterval(timeInterval);
      clearInterval(notificationInterval);
    };
  }, [generateNotification, isInitialized]);

  const formatTime = useCallback((seconds: number): string => {
    if (seconds < 60) return 'chiar acum';
    const minutes = Math.floor(seconds / 60);
    return `acum ${minutes} ${minutes === 1 ? 'minut' : 'minute'}`;
  }, []);

  return (
    <div className={`fixed bottom-4 left-4 z-40 transition-all duration-500 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-full'}`}>
      {notifications.slice(-1).map((notification) => (
        <div
          key={notification.id}
          className="bg-gradient-to-r from-gray-900/95 to-black/95 backdrop-blur-lg rounded-lg border border-red-500/20 p-3 sm:p-4 mb-2 shadow-lg animate-slide-in max-w-[280px] sm:max-w-xs transform transition-all duration-500 hover:border-red-500/40 hover:shadow-[0_0_30px_rgba(239,68,68,0.2)]"
        >
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <div>
              <p className="text-sm text-gray-300">
                <span className="font-semibold text-white">{notification.name}</span> din{' '}
                <span className="text-white">{notification.city}</span> {notification.message}
              </p>
              <p className="text-xs text-gray-400 mt-1">{formatTime(notification.time)}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
